import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { useToast } from '../hooks/use-toast';
import {
  ArrowLeft, PhoneOutgoing, Phone, Plus, Play, Pause, Trash2,
  Users, Search, ChevronRight, Clock, Target, X, UserPlus,
  Sparkles, Loader2, Bot, Zap, PhoneCall, Activity, PhoneOff,
  History, CheckCircle2, AlertTriangle, MoreVertical, Calendar, Edit3, Check, Copy as CopyIcon,
  MessageSquare, Mail, Send
} from 'lucide-react';
import axios from 'axios';

import { BACKEND_URL } from "../lib/backendUrl";
const API = `${BACKEND_URL}/api`;

const STATUS_BADGE = {
  draft: 'bg-gray-500/15 text-gray-400 border-gray-500/30',
  active: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  paused: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  completed: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
};

const CONTACT_STATUS_BADGE = {
  pending: { label: 'Pending', cls: 'bg-gray-500/15 text-gray-400 border-gray-500/20' },
  called: { label: 'Called', cls: 'bg-blue-500/15 text-blue-400 border-blue-500/20' },
  answered: { label: 'Answered', cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20' },
  voicemail: { label: 'Voicemail', cls: 'bg-amber-500/15 text-amber-400 border-amber-500/20' },
  no_answer: { label: 'No Answer', cls: 'bg-red-500/15 text-red-400 border-red-500/20' },
  follow_up: { label: 'Follow Up', cls: 'bg-purple-500/15 text-purple-400 border-purple-500/20' },
  completed: { label: 'Completed', cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20' },
  sent: { label: 'Sent', cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20' },
  failed: { label: 'Failed', cls: 'bg-red-500/15 text-red-400 border-red-500/20' },
};

const TYPE_META = {
  call: { label: 'Call', cls: 'bg-purple-500/10 text-purple-400 border-purple-500/20' },
  sms: { label: 'SMS', cls: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20' },
  email: { label: 'Email', cls: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
};

const OutboundCalls = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const headers = { Authorization: `Bearer ${token}` };

  const [view, setView] = useState('campaigns');
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState({});
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [detailContacts, setDetailContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [creating, setCreating] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    name: '', goal: 'generate_leads', target_audience: '', ai_script: '',
    campaign_type: 'call', mode: 'bulk', drip_interval_minutes: 30,
    send_window_start: '09:00', send_window_end: '17:00',
    send_days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
  });
  const [generatingScript, setGeneratingScript] = useState(false);
  const [editingCampaignId, setEditingCampaignId] = useState(null);

  const startEditCampaign = (camp) => {
    setNewCampaign({
      name: camp.name || '',
      goal: camp.goal || 'generate_leads',
      target_audience: camp.target_audience || '',
      ai_script: camp.ai_script || '',
      campaign_type: camp.campaign_type || 'call',
      mode: camp.mode || 'bulk',
      drip_interval_minutes: camp.drip_interval_minutes || 30,
      send_window_start: camp.send_window_start || '09:00',
      send_window_end: camp.send_window_end || '17:00',
      send_days: camp.send_days || ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
    });
    setEditingCampaignId(camp.id);
    setView('create');
  };

  // Quick call
  const [quickCallNumber, setQuickCallNumber] = useState('');
  const [quickCallScript, setQuickCallScript] = useState('');
  const [initiatingCall, setInitiatingCall] = useState(false);
  const [activeOutboundCalls, setActiveOutboundCalls] = useState([]);
  const [searchParams] = useSearchParams();
  const [quickCallContactName, setQuickCallContactName] = useState('');

  useEffect(() => {
    if (searchParams.get('quickcall') === '1') {
      setView('quick-call');
      const phone = searchParams.get('phone');
      const name = searchParams.get('name');
      if (phone) setQuickCallNumber(phone);
      if (name) setQuickCallContactName(name);
    }
  }, [searchParams]);

  // Call history
  const [callHistory, setCallHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // CRM contact picker
  const [showContactPicker, setShowContactPicker] = useState(false);
  const [crmContacts, setCrmContacts] = useState([]);
  const [crmSearch, setCrmSearch] = useState('');
  const [selectedContactIds, setSelectedContactIds] = useState(new Set());
  const [smartLists, setSmartLists] = useState([]);
  const [pickerTab, setPickerTab] = useState('contacts'); // contacts or smart-lists

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [campRes, statsRes, activeRes] = await Promise.allSettled([
        axios.get(`${API}/outreach/campaigns`, { headers }),
        axios.get(`${API}/outreach/stats`, { headers }),
        axios.get(`${API}/realtime-voice/active-calls`, { headers }),
      ]);
      if (campRes.status === 'fulfilled') setCampaigns(campRes.value.data);
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
      if (activeRes.status === 'fulfilled') {
        setActiveOutboundCalls((activeRes.value.data.calls || []).filter(c => c.direction === 'outbound'));
      }
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/realtime-voice/active-calls`, { headers });
        setActiveOutboundCalls((res.data.calls || []).filter(c => c.direction === 'outbound'));
      } catch {}
    }, 10000);
    return () => clearInterval(interval);
  }, [token]);

  const fetchCallHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/outreach/call-history`, { headers });
      setCallHistory(res.data);
    } catch {}
    setHistoryLoading(false);
  };

  const fetchCrmContacts = async () => {
    try {
      const res = await axios.get(`${API}/crm/contacts`, { headers, params: crmSearch ? { search: crmSearch } : {} });
      setCrmContacts(res.data);
    } catch {}
  };

  const fetchSmartLists = async () => {
    try {
      const res = await axios.get(`${API}/crm/smart-lists`, { headers });
      setSmartLists(res.data);
    } catch {}
  };

  const enrollSmartList = async (listId) => {
    if (!selectedCampaign) return;
    try {
      const res = await axios.get(`${API}/crm/smart-lists/${listId}/contact-ids`, { headers });
      const ids = res.data.contact_ids || [];
      if (ids.length === 0) { toast({ title: 'No contacts in this list', variant: 'destructive' }); return; }
      await axios.post(`${API}/outreach/campaigns/${selectedCampaign.id}/enroll`, { contact_ids: ids }, { headers });
      toast({ title: `${ids.length} contacts enrolled from Smart List` });
      setShowContactPicker(false);
      openCampaign(selectedCampaign);
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail, variant: 'destructive' });
    }
  };

  const createCampaign = async () => {
    if (!newCampaign.name.trim()) { toast({ title: 'Campaign name required', variant: 'destructive' }); return; }
    setCreating(true);
    try {
      const payload = {
        name: newCampaign.name,
        goal: newCampaign.goal,
        target_audience: newCampaign.target_audience,
        ai_script: newCampaign.ai_script,
        campaign_type: newCampaign.campaign_type,
        mode: newCampaign.mode,
        drip_interval_minutes: newCampaign.drip_interval_minutes,
        send_window_start: newCampaign.send_window_start,
        send_window_end: newCampaign.send_window_end,
        send_days: newCampaign.send_days,
      };
      if (editingCampaignId) {
        const res = await axios.put(`${API}/outreach/campaigns/${editingCampaignId}`, payload, { headers });
        toast({ title: 'Campaign updated' });
        if (selectedCampaign?.id === editingCampaignId) setSelectedCampaign(res.data);
      } else {
        await axios.post(`${API}/outreach/campaigns`, { ...payload, sequence_steps: [] }, { headers });
        toast({ title: 'Campaign created' });
      }
      setNewCampaign({ name: '', goal: 'generate_leads', target_audience: '', ai_script: '', mode: 'bulk', drip_interval_minutes: 30, send_window_start: '09:00', send_window_end: '17:00', send_days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'] });
      setView(editingCampaignId && selectedCampaign?.id === editingCampaignId ? 'detail' : 'campaigns');
      setEditingCampaignId(null);
      fetchAll();
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail, variant: 'destructive' });
    } finally { setCreating(false); }
  };

  const duplicateCampaign = async (campId) => {
    try {
      const res = await axios.post(`${API}/outreach/campaigns/${campId}/duplicate`, {}, { headers });
      toast({ title: 'Campaign duplicated', description: `"${res.data.name}" created as draft — tweak and activate when ready.` });
      fetchAll();
      startEditCampaign(res.data);
    } catch (err) {
      toast({ title: 'Failed to duplicate', description: err.response?.data?.detail, variant: 'destructive' });
    }
  };

  const [sendingMessages, setSendingMessages] = useState(false);
  const sendCampaignMessages = async (contactIds = null) => {
    if (!selectedCampaign) return;
    setSendingMessages(true);
    try {
      const res = await axios.post(`${API}/outreach/campaigns/${selectedCampaign.id}/send-messages`, contactIds ? { contact_ids: contactIds } : {}, { headers });
      const { sent, failed } = res.data;
      toast({ title: `${sent} message${sent !== 1 ? 's' : ''} sent${failed ? `, ${failed} failed` : ''}`, variant: failed && !sent ? 'destructive' : undefined });
      openCampaign(selectedCampaign);
      fetchAll();
    } catch (err) {
      toast({ title: 'Failed to send', description: err.response?.data?.detail, variant: 'destructive' });
    } finally { setSendingMessages(false); }
  };

  const removeContactFromCampaign = async (contactId) => {
    try {
      await axios.post(`${API}/outreach/campaigns/${selectedCampaign.id}/unenroll`, { contact_ids: [contactId] }, { headers });
      const res = await axios.get(`${API}/outreach/campaigns/${selectedCampaign.id}`, { headers });
      setSelectedCampaign(res.data.campaign || res.data);
      setDetailContacts(res.data.enrolled_contacts || []);
      toast({ title: 'Contact removed from campaign' });
      fetchAll();
    } catch (err) {
      toast({ title: 'Failed to remove contact', description: err.response?.data?.detail, variant: 'destructive' });
    }
  };

  const openCampaign = async (camp) => {
    try {
      const res = await axios.get(`${API}/outreach/campaigns/${camp.id}`, { headers });
      setSelectedCampaign(res.data.campaign || res.data);
      setDetailContacts(res.data.enrolled_contacts || []);
      setView('detail');
    } catch {
      toast({ title: 'Failed to load campaign', variant: 'destructive' });
    }
  };

  const toggleCampaign = async (campId, currentStatus) => {
    try {
      const action = currentStatus === 'active' ? 'pause' : 'activate';
      await axios.post(`${API}/outreach/campaigns/${campId}/${action}`, {}, { headers });
      toast({ title: `Campaign ${action === 'pause' ? 'paused' : 'activated'}` });
      fetchAll();
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail, variant: 'destructive' });
    }
  };

  const deleteCampaign = async (campId) => {
    try {
      await axios.delete(`${API}/outreach/campaigns/${campId}`, { headers });
      toast({ title: 'Campaign deleted' });
      if (selectedCampaign?.id === campId) setView('campaigns');
      fetchAll();
    } catch { toast({ title: 'Failed to delete', variant: 'destructive' }); }
  };

  const generateAIScript = async () => {
    if (!newCampaign.target_audience.trim()) { toast({ title: 'Enter target audience first', variant: 'destructive' }); return; }
    setGeneratingScript(true);
    try {
      const res = await axios.post(`${API}/outreach/ai-sequence`, {
        goal: newCampaign.goal, target_audience: newCampaign.target_audience, tone: 'professional', num_steps: 3,
      }, { headers });
      if (res.data?.steps?.length) {
        setNewCampaign(p => ({ ...p, ai_script: res.data.steps.map(s => `Step ${s.order}: [${s.channel}] ${s.subject || ''}\n${s.content}`).join('\n\n') }));
      }
      toast({ title: 'AI script generated' });
    } catch { toast({ title: 'Failed to generate', variant: 'destructive' }); }
    finally { setGeneratingScript(false); }
  };

  const initiateQuickCall = async (number, script) => {
    const num = number || quickCallNumber;
    if (!num.trim()) { toast({ title: 'Enter a phone number', variant: 'destructive' }); return; }
    setInitiatingCall(true);
    try {
      await axios.post(`${API}/realtime-voice/outbound/initiate`, {
        to_number: num.startsWith('+') ? num : `+1${num.replace(/\D/g, '')}`,
        ai_script: script || quickCallScript || undefined,
        agent_name: 'Mia',
      }, { headers });
      toast({ title: 'Call initiated!', description: `Calling ${num}...` });
      setQuickCallNumber(''); setQuickCallScript('');
      fetchAll();
    } catch (err) {
      toast({ title: 'Failed to call', description: err.response?.data?.detail, variant: 'destructive' });
    } finally { setInitiatingCall(false); }
  };

  const endCall = async (callSid) => {
    try {
      await axios.post(`${API}/realtime-voice/end-call/${callSid}`, {}, { headers });
      toast({ title: 'Call ended' });
      setActiveOutboundCalls(prev => prev.filter(c => c.id !== callSid));
    } catch { toast({ title: 'Failed to end call', variant: 'destructive' }); }
  };

  const enrollContacts = async () => {
    if (!selectedCampaign || selectedContactIds.size === 0) return;
    try {
      await axios.post(`${API}/outreach/campaigns/${selectedCampaign.id}/enroll`, { contact_ids: [...selectedContactIds] }, { headers });
      toast({ title: `${selectedContactIds.size} contacts added` });
      setShowContactPicker(false); setSelectedContactIds(new Set());
      openCampaign(selectedCampaign);
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail, variant: 'destructive' });
    }
  };

  const updateContactStatus = async (contactId, status) => {
    if (!selectedCampaign) return;
    try {
      await axios.put(`${API}/outreach/campaigns/${selectedCampaign.id}/contact-status/${contactId}`, { status }, { headers });
      setSelectedCampaign(prev => ({
        ...prev,
        contact_statuses: { ...prev.contact_statuses, [contactId]: { ...(prev.contact_statuses?.[contactId] || {}), status } }
      }));
    } catch {}
  };

  const filteredCampaigns = campaigns.filter(c => !searchTerm || (c.name || '').toLowerCase().includes(searchTerm.toLowerCase()));

  const tabs = [
    { key: 'campaigns', label: 'Campaigns', icon: Target },
    { key: 'quick-call', label: 'Quick Call', icon: PhoneCall },
    { key: 'call-history', label: 'Call Log', icon: History },
    { key: 'create', label: 'New Campaign', icon: Plus },
  ];

  return (
    <div className="min-h-screen bg-[#0a1628] text-white" data-testid="outbound-calls-page">
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          {view === 'detail' && (
            <Button variant="ghost" onClick={() => setView('campaigns')} className="text-gray-400 hover:text-white p-2" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          )}
          <div className="flex-1">
            <h1 className="text-2xl font-bold" data-testid="outbound-calls-title">Outbound Calls</h1>
            <p className="text-gray-500 text-sm">AI-powered outreach — bulk or drip campaigns & quick calls</p>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/20" data-testid="outbound-realtime-badge">
            <Zap className="w-3.5 h-3.5 text-purple-400" />
            <span className="text-xs text-purple-400 font-medium">Real-time AI</span>
          </div>
        </div>

        {/* Active Calls Banner */}
        {activeOutboundCalls.length > 0 && (
          <Card className="bg-purple-500/5 border-purple-500/20 mb-6" data-testid="active-outbound-calls">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <Activity className="w-5 h-5 text-purple-400 animate-pulse" />
                <span className="text-sm font-medium text-purple-400">{activeOutboundCalls.length} Active Call{activeOutboundCalls.length > 1 ? 's' : ''}</span>
              </div>
              {activeOutboundCalls.map(call => (
                <div key={call.id} className="flex items-center justify-between p-3 rounded-lg bg-white/[0.03]" data-testid={`active-outbound-${call.id}`}>
                  <div className="flex items-center gap-3">
                    <PhoneOutgoing className="w-4 h-4 text-purple-400" />
                    <div>
                      <p className="text-sm text-white font-medium">{call.called_number || 'Unknown'}</p>
                      <p className="text-xs text-gray-500">Started {call.created_at ? new Date(call.created_at).toLocaleTimeString() : ''}</p>
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => endCall(call.id)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10" data-testid={`end-outbound-${call.id}`}>
                    <PhoneOff className="w-4 h-4 mr-1" /> End
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="outbound-analytics">
          {[
            { v: stats.total_campaigns || campaigns.length, l: 'Total Campaigns', c: '' },
            { v: stats.active_campaigns || campaigns.filter(c => c.status === 'active').length, l: 'Active', c: 'text-emerald-400' },
            { v: stats.total_enrolled || 0, l: 'Contacts Enrolled', c: 'text-blue-400' },
            { v: stats.calls_made || 0, l: 'Calls Made', c: 'text-purple-400' },
          ].map((s, i) => (
            <Card key={i} className="bg-white/[0.03] border-white/[0.06]"><CardContent className="p-4">
              <p className={`text-2xl font-bold ${s.c}`}>{s.v}</p>
              <p className="text-xs text-gray-500">{s.l}</p>
            </CardContent></Card>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-white/[0.03] rounded-lg p-1 w-fit" data-testid="outbound-tabs">
          {tabs.map(tab => (
            <button key={tab.key} onClick={() => { setView(tab.key); if (tab.key === 'create') setEditingCampaignId(null); if (tab.key === 'call-history') fetchCallHistory(); }} data-testid={`tab-${tab.key}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${view === tab.key || (view === 'detail' && tab.key === 'campaigns') ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'}`}>
              <tab.icon className="w-4 h-4" /> {tab.label}
            </button>
          ))}
        </div>

        {/* QUICK CALL */}
        {view === 'quick-call' && (
          <div className="max-w-2xl" data-testid="quick-call-view">
            <Card className="bg-white/[0.03] border-white/[0.06]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <PhoneCall className="w-5 h-5 text-purple-400" /> Quick AI Call
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 ml-2">Real-time</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                {quickCallContactName && (
                  <div className="flex items-center gap-2 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg" data-testid="quick-call-contact-banner">
                    <PhoneCall className="w-4 h-4 text-blue-400" />
                    <p className="text-sm text-blue-300">Calling <span className="font-semibold text-white">{quickCallContactName}</span> from your CRM — review the number and script, then start the call.</p>
                  </div>
                )}
                <div className="p-3 bg-purple-500/5 border border-purple-500/20 rounded-lg">
                  <div className="flex items-center gap-2 mb-1"><Zap className="w-4 h-4 text-purple-400" /><p className="text-sm font-medium text-purple-400">AI-Powered Outbound Call</p></div>
                  <p className="text-xs text-gray-400">Enter a phone number and optional script. Mia will call and have a natural conversation using real-time voice AI.</p>
                </div>
                <div>
                  <Label className="text-gray-300 text-sm">Phone Number</Label>
                  <Input value={quickCallNumber} onChange={e => setQuickCallNumber(e.target.value)} className="bg-black/30 border-white/10 text-white mt-1" data-testid="quick-call-number" placeholder="+1 (555) 123-4567" />
                  <p className="text-gray-600 text-xs mt-1">Include country code (e.g., +1 for US)</p>
                </div>
                <div>
                  <Label className="text-gray-300 text-sm">AI Script (Optional)</Label>
                  <Textarea value={quickCallScript} onChange={e => setQuickCallScript(e.target.value)} className="bg-black/30 border-white/10 text-white mt-1 min-h-[100px]" data-testid="quick-call-script" placeholder="e.g., Follow up on their inquiry about our premium plan..." />
                </div>
                <Button onClick={() => initiateQuickCall()} disabled={initiatingCall || !quickCallNumber.trim()} className="bg-purple-600 hover:bg-purple-700 text-white w-full" data-testid="initiate-call-btn">
                  {initiatingCall ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <PhoneCall className="w-4 h-4 mr-2" />}
                  {initiatingCall ? 'Initiating...' : 'Start AI Call'}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* CALL HISTORY */}
        {view === 'call-history' && (
          <div data-testid="call-history-view">
            {historyLoading ? <p className="text-gray-500 text-sm text-center py-12">Loading call history...</p> :
              callHistory.length === 0 ? (
                <Card className="bg-white/[0.03] border-white/[0.06]"><CardContent className="py-16 text-center">
                  <History className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                  <h3 className="text-base text-gray-400 font-semibold mb-1">No outbound call history</h3>
                  <p className="text-sm text-gray-500">Calls made via Quick Call or campaigns will appear here.</p>
                </CardContent></Card>
              ) : (
                <div className="bg-gray-900/30 border border-white/10 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left px-4 py-3 text-gray-400 font-medium text-xs">Number</th>
                        <th className="text-left px-4 py-3 text-gray-400 font-medium text-xs">Status</th>
                        <th className="text-left px-4 py-3 text-gray-400 font-medium text-xs">Duration</th>
                        <th className="text-left px-4 py-3 text-gray-400 font-medium text-xs">Date</th>
                        <th className="text-left px-4 py-3 text-gray-400 font-medium text-xs">Summary</th>
                      </tr>
                    </thead>
                    <tbody>
                      {callHistory.map((call, i) => (
                        <tr key={call.id || i} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`call-log-${i}`}>
                          <td className="px-4 py-3 text-white font-medium">{call.caller_number || call.called_number || '—'}</td>
                          <td className="px-4 py-3">
                            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${call.status === 'completed' ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20' : call.status === 'missed' ? 'bg-red-500/15 text-red-400 border-red-500/20' : 'bg-gray-500/15 text-gray-400 border-gray-500/20'}`}>
                              {call.status || 'unknown'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-400">{call.duration ? `${Math.floor(call.duration / 60)}m ${call.duration % 60}s` : '—'}</td>
                          <td className="px-4 py-3 text-gray-400 text-xs">{call.created_at ? new Date(call.created_at).toLocaleString() : '—'}</td>
                          <td className="px-4 py-3 text-gray-400 text-xs truncate max-w-[200px]">{call.summary || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </div>
        )}

        {/* CAMPAIGNS LIST */}
        {view === 'campaigns' && (
          <div data-testid="campaigns-view">
            <div className="flex items-center gap-3 mb-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search campaigns..." className="pl-10 bg-white/[0.03] border-white/[0.06] text-white" data-testid="search-campaigns" />
              </div>
            </div>
            {loading ? <p className="text-gray-500 text-sm py-8 text-center">Loading...</p> :
              filteredCampaigns.length === 0 ? (
                <Card className="bg-white/[0.03] border-white/[0.06]"><CardContent className="py-16 text-center">
                  <PhoneOutgoing className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                  <h3 className="text-base font-semibold text-gray-400 mb-1">No outbound campaigns yet</h3>
                  <p className="text-sm text-gray-500 mb-4">Create a campaign or use Quick Call to reach out.</p>
                  <div className="flex gap-3 justify-center">
                    <Button onClick={() => setView('quick-call')} className="bg-purple-600 hover:bg-purple-700 text-white"><PhoneCall className="w-4 h-4 mr-2" /> Quick Call</Button>
                    <Button onClick={() => { setEditingCampaignId(null); setView('create'); }} variant="outline" className="border-white/10 text-gray-300 hover:bg-white/5"><Plus className="w-4 h-4 mr-2" /> New Campaign</Button>
                  </div>
                </CardContent></Card>
              ) : (
                <div className="space-y-2">
                  {filteredCampaigns.map(camp => (
                    <Card key={camp.id} className="bg-white/[0.03] border-white/[0.06] hover:border-white/[0.12] transition-colors cursor-pointer" data-testid={`campaign-${camp.id}`}>
                      <CardContent className="p-4 flex items-center gap-4" onClick={() => openCampaign(camp)}>
                        <div className="w-10 h-10 rounded-full bg-purple-600/20 flex items-center justify-center text-purple-400 flex-shrink-0">
                          <Target className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-white truncate">{camp.name}</p>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS_BADGE[camp.status] || STATUS_BADGE.draft}`}>{camp.status || 'draft'}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded border ${(TYPE_META[camp.campaign_type] || TYPE_META.call).cls}`} data-testid={`type-badge-${camp.id}`}>
                              {(TYPE_META[camp.campaign_type] || TYPE_META.call).label}
                            </span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded border ${camp.mode === 'drip' ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20' : 'bg-orange-500/10 text-orange-400 border-orange-500/20'}`}>
                              {camp.mode === 'drip' ? 'Drip' : 'Bulk'}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5">{camp.goal?.replace(/_/g, ' ') || 'No goal'} &middot; {(camp.enrolled_contacts || []).length} contacts</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button onClick={e => { e.stopPropagation(); startEditCampaign(camp); }}
                            className="text-gray-500 hover:text-blue-400 transition-colors p-1.5 rounded-lg hover:bg-blue-500/10"
                            data-testid={`edit-${camp.id}`} title="Edit campaign">
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button onClick={e => { e.stopPropagation(); duplicateCampaign(camp.id); }}
                            className="text-gray-500 hover:text-cyan-400 transition-colors p-1.5 rounded-lg hover:bg-cyan-500/10"
                            data-testid={`duplicate-${camp.id}`} title="Duplicate campaign">
                            <CopyIcon className="w-4 h-4" />
                          </button>
                          <button onClick={e => { e.stopPropagation(); toggleCampaign(camp.id, camp.status); }}
                            className={`p-1.5 rounded-lg transition-colors ${camp.status === 'active' ? 'text-amber-400 hover:bg-amber-500/10' : 'text-emerald-400 hover:bg-emerald-500/10'}`}
                            data-testid={`toggle-${camp.id}`}>
                            {camp.status === 'active' ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                          </button>
                          <button onClick={e => { e.stopPropagation(); deleteCampaign(camp.id); }} className="text-gray-600 hover:text-red-400 transition-colors p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                          <ChevronRight className="w-4 h-4 text-gray-600" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
          </div>
        )}

        {/* CAMPAIGN DETAIL */}
        {view === 'detail' && selectedCampaign && (
          <div className="space-y-6" data-testid="campaign-detail">
            {/* Campaign Header */}
            <Card className="bg-white/[0.03] border-white/[0.06]">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h2 className="text-lg font-bold text-white">{selectedCampaign.name}</h2>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS_BADGE[selectedCampaign.status] || STATUS_BADGE.draft}`}>{selectedCampaign.status}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded border ${(TYPE_META[selectedCampaign.campaign_type] || TYPE_META.call).cls}`} data-testid="detail-type-badge">
                        {(TYPE_META[selectedCampaign.campaign_type] || TYPE_META.call).label}
                      </span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded border ${selectedCampaign.mode === 'drip' ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20' : 'bg-orange-500/10 text-orange-400 border-orange-500/20'}`}>
                        {selectedCampaign.mode === 'drip' ? 'Drip Mode' : 'Bulk Mode'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">
                      {selectedCampaign.goal?.replace(/_/g, ' ')} &middot;
                      {(selectedCampaign.send_days || []).join(', ')} &middot;
                      {selectedCampaign.send_window_start}–{selectedCampaign.send_window_end}
                      {selectedCampaign.mode === 'drip' && ` · ${selectedCampaign.drip_interval_minutes}min between calls`}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {['sms', 'email'].includes(selectedCampaign.campaign_type) && (
                      <Button size="sm" onClick={() => sendCampaignMessages()} disabled={sendingMessages || (selectedCampaign.enrolled_contacts || []).length === 0}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="send-all-messages-btn">
                        {sendingMessages ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />} Send to All
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => duplicateCampaign(selectedCampaign.id)} className="border-white/10 text-gray-300" data-testid="duplicate-campaign-btn">
                      <CopyIcon className="w-3.5 h-3.5 mr-1" /> Duplicate
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => startEditCampaign(selectedCampaign)} className="border-white/10 text-gray-300" data-testid="edit-campaign-btn">
                      <Edit3 className="w-3.5 h-3.5 mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => toggleCampaign(selectedCampaign.id, selectedCampaign.status)} className="border-white/10 text-gray-300">
                      {selectedCampaign.status === 'active' ? <><Pause className="w-3.5 h-3.5 mr-1" /> Pause</> : <><Play className="w-3.5 h-3.5 mr-1" /> Activate</>}
                    </Button>
                    <Button size="sm" onClick={() => { setShowContactPicker(true); fetchCrmContacts(); fetchSmartLists(); setPickerTab('contacts'); }} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="add-contacts-btn">
                      <UserPlus className="w-3.5 h-3.5 mr-1" /> Add Contacts
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Campaign Results */}
            <Card className="bg-white/[0.03] border-white/[0.06]" data-testid="campaign-results-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-sm flex items-center gap-2"><Activity className="w-4 h-4 text-emerald-400" /> Campaign Results</CardTitle>
              </CardHeader>
              <CardContent>
                {(() => {
                  const statuses = Object.values(selectedCampaign.contact_statuses || {});
                  const enrolled = (selectedCampaign.enrolled_contacts || []).length;
                  const countOf = (k) => statuses.filter(s => s.status === k).length;
                  const explicit = statuses.filter(s => s.status && s.status !== 'pending').length;
                  const isMsg = ['sms', 'email'].includes(selectedCampaign.campaign_type);
                  const results = isMsg ? [
                    { key: 'sent', label: 'Sent', color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20' },
                    { key: 'failed', label: 'Failed', color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/20' },
                    { key: 'follow_up', label: 'Follow Up', color: 'text-purple-400', bg: 'bg-purple-500/10 border-purple-500/20' },
                    { key: 'completed', label: 'Booked / Completed', color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20' },
                  ] : [
                    { key: 'answered', label: 'Answered', color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20' },
                    { key: 'completed', label: 'Booked / Completed', color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20' },
                    { key: 'follow_up', label: 'Follow Up', color: 'text-purple-400', bg: 'bg-purple-500/10 border-purple-500/20' },
                    { key: 'voicemail', label: 'Voicemail', color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20' },
                    { key: 'no_answer', label: 'No Answer', color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/20' },
                  ];
                  return (
                    <div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                        <div className="p-3 rounded-lg border bg-white/[0.03] border-white/10 text-center" data-testid="result-enrolled">
                          <p className="text-xl font-bold text-white">{enrolled}</p>
                          <p className="text-[10px] text-gray-500 mt-0.5">Enrolled</p>
                        </div>
                        {results.map(r => (
                          <div key={r.key} className={`p-3 rounded-lg border text-center ${r.bg}`} data-testid={`result-${r.key}`}>
                            <p className={`text-xl font-bold ${r.color}`}>{countOf(r.key)}</p>
                            <p className="text-[10px] text-gray-500 mt-0.5">{r.label}</p>
                          </div>
                        ))}
                      </div>
                      {explicit === 0 && (
                        <p className="text-xs text-gray-500 mt-3">No outcomes recorded yet — results fill in as {['sms', 'email'].includes(selectedCampaign.campaign_type) ? 'messages are sent' : 'calls are made'} and contact statuses update.</p>
                      )}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            {/* Enrolled Contacts */}
            <Card className="bg-white/[0.03] border-white/[0.06]">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-sm flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-400" /> Enrolled Contacts ({(selectedCampaign.enrolled_contacts || []).length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {detailContacts.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No contacts enrolled yet</p>
                    <Button size="sm" onClick={() => { setShowContactPicker(true); fetchCrmContacts(); fetchSmartLists(); setPickerTab('contacts'); }} className="mt-3 bg-purple-600 hover:bg-purple-700 text-white text-xs">
                      <UserPlus className="w-3 h-3 mr-1" /> Add from CRM
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {detailContacts.map(contact => {
                      const cs = selectedCampaign.contact_statuses?.[contact.id] || {};
                      const csBadge = CONTACT_STATUS_BADGE[cs.status] || CONTACT_STATUS_BADGE.pending;
                      return (
                        <div key={contact.id} className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.02] hover:bg-white/[0.04] transition-colors" data-testid={`enrolled-${contact.id}`}>
                          <div className="w-8 h-8 rounded-full bg-blue-600/20 flex items-center justify-center text-blue-400 text-xs font-bold flex-shrink-0">
                            {(contact.name || '?')[0].toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white font-medium truncate">{contact.name || 'Unknown'}</p>
                            <p className="text-xs text-gray-500">{contact.phone || contact.email || '—'}</p>
                          </div>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full border ${csBadge.cls}`}>{csBadge.label}</span>
                          <select
                            value={cs.status || 'pending'}
                            onChange={e => updateContactStatus(contact.id, e.target.value)}
                            className="bg-black/30 border border-white/10 text-gray-300 text-xs rounded px-2 py-1 outline-none"
                            data-testid={`status-select-${contact.id}`}
                          >
                            {Object.entries(CONTACT_STATUS_BADGE).map(([k, v]) => (
                              <option key={k} value={k}>{v.label}</option>
                            ))}
                          </select>
                          {['sms', 'email'].includes(selectedCampaign.campaign_type) ? (
                            <Button size="sm" variant="ghost" onClick={() => sendCampaignMessages([contact.id])} disabled={sendingMessages}
                              className="text-emerald-400 hover:bg-emerald-500/10 h-7 w-7 p-0" title={`Send ${selectedCampaign.campaign_type === 'sms' ? 'SMS' : 'email'}`} data-testid={`send-contact-${contact.id}`}>
                              <Send className="w-3.5 h-3.5" />
                            </Button>
                          ) : contact.phone && (
                            <Button size="sm" variant="ghost" onClick={() => initiateQuickCall(contact.phone)} className="text-purple-400 hover:bg-purple-500/10 h-7 w-7 p-0" data-testid={`call-contact-${contact.id}`}>
                              <PhoneCall className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => removeContactFromCampaign(contact.id)} className="text-gray-500 hover:text-red-400 hover:bg-red-500/10 h-7 w-7 p-0" title="Remove from campaign" data-testid={`remove-contact-${contact.id}`}>
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Sequence Steps */}
            {(selectedCampaign.sequence_steps || []).length > 0 && (
              <Card className="bg-white/[0.03] border-white/[0.06]">
                <CardHeader className="pb-2"><CardTitle className="text-white text-sm">Outreach Sequence</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {selectedCampaign.sequence_steps.map((step, i) => (
                      <div key={i} className="p-3 bg-white/[0.03] rounded-lg border border-white/5">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-[10px] font-bold text-gray-500">STEP {step.order || step.step_number || i + 1}</span>
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400">{step.channel}</span>
                          {step.delay_days > 0 && <span className="text-[10px] text-gray-600 flex items-center gap-1"><Clock className="w-3 h-3" />+{step.delay_days}d</span>}
                        </div>
                        {step.subject && <p className="text-sm text-white font-medium mb-1">{step.subject}</p>}
                        <p className="text-xs text-gray-400 leading-relaxed">{step.content?.substring(0, 200)}{step.content?.length > 200 ? '...' : ''}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Contact Picker Modal */}
            {showContactPicker && (
              <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center" data-testid="contact-picker-modal">
                <Card className="bg-[#0d1f35] border-white/10 w-full max-w-lg max-h-[70vh] flex flex-col">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-white text-sm">Add Contacts to Campaign</CardTitle>
                      <Button size="sm" variant="ghost" onClick={() => setShowContactPicker(false)} className="text-gray-400 h-7 w-7 p-0"><X className="w-4 h-4" /></Button>
                    </div>
                    {/* Picker Tabs */}
                    <div className="flex gap-1 mt-2 bg-white/[0.03] rounded-md p-0.5">
                      <button onClick={() => setPickerTab('contacts')} className={`flex-1 text-xs py-1.5 rounded font-medium transition-all ${pickerTab === 'contacts' ? 'bg-blue-600 text-white' : 'text-gray-400'}`} data-testid="picker-tab-contacts">
                        <Users className="w-3 h-3 inline mr-1" /> Contacts
                      </button>
                      <button onClick={() => setPickerTab('smart-lists')} className={`flex-1 text-xs py-1.5 rounded font-medium transition-all ${pickerTab === 'smart-lists' ? 'bg-blue-600 text-white' : 'text-gray-400'}`} data-testid="picker-tab-lists">
                        <Target className="w-3 h-3 inline mr-1" /> Smart Lists
                      </button>
                    </div>
                    {pickerTab === 'contacts' && (
                      <div className="relative mt-2">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                        <Input value={crmSearch} onChange={e => { setCrmSearch(e.target.value); fetchCrmContacts(); }} placeholder="Search contacts..." className="pl-10 bg-black/30 border-white/10 text-white h-8 text-xs" />
                      </div>
                    )}
                  </CardHeader>
                  <CardContent className="flex-1 overflow-y-auto space-y-1">
                    {pickerTab === 'contacts' ? (
                      crmContacts.length === 0 ? (
                        <p className="text-gray-500 text-xs text-center py-6">No contacts found</p>
                      ) : crmContacts.map(c => {
                        const alreadyEnrolled = (selectedCampaign.enrolled_contacts || []).includes(c.id);
                        return (
                          <label key={c.id} className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${alreadyEnrolled ? 'opacity-40' : 'hover:bg-white/[0.04]'}`}>
                            <Checkbox
                              checked={selectedContactIds.has(c.id)}
                              onCheckedChange={() => {
                                if (alreadyEnrolled) return;
                                setSelectedContactIds(prev => { const n = new Set(prev); n.has(c.id) ? n.delete(c.id) : n.add(c.id); return n; });
                              }}
                              disabled={alreadyEnrolled}
                              className="border-white/20 data-[state=checked]:bg-purple-600"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-white font-medium truncate">{c.name || 'Unknown'}</p>
                              <p className="text-xs text-gray-500">{c.phone || ''} {c.email ? `· ${c.email}` : ''}</p>
                            </div>
                            {alreadyEnrolled && <span className="text-[10px] text-gray-500">Already added</span>}
                          </label>
                        );
                      })
                    ) : (
                      smartLists.length === 0 ? (
                        <div className="text-center py-6">
                          <p className="text-gray-500 text-xs">No Smart Lists yet</p>
                          <p className="text-gray-600 text-[10px] mt-1">Create Smart Lists in CRM by tagging contacts</p>
                        </div>
                      ) : smartLists.map(sl => (
                        <div key={sl.id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/[0.04] transition-colors" data-testid={`picker-list-${sl.id}`}>
                          <div className="w-8 h-8 rounded-full bg-purple-600/20 flex items-center justify-center text-purple-400 flex-shrink-0">
                            <Target className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white font-medium">{sl.name}</p>
                            <div className="flex gap-1 mt-0.5">
                              {sl.filters?.tag && <span className="text-[9px] px-1 py-0.5 rounded bg-purple-500/10 text-purple-400">Tag: {sl.filters.tag}</span>}
                              {sl.filters?.status && <span className="text-[9px] px-1 py-0.5 rounded bg-blue-500/10 text-blue-400">Status: {sl.filters.status}</span>}
                              {sl.filters?.source && <span className="text-[9px] px-1 py-0.5 rounded bg-amber-500/10 text-amber-400">Source: {sl.filters.source}</span>}
                            </div>
                          </div>
                          <span className="text-xs text-gray-500 mr-2">{sl.contact_count} contacts</span>
                          <Button size="sm" onClick={() => enrollSmartList(sl.id)} className="bg-purple-600 hover:bg-purple-700 text-white text-xs" data-testid={`enroll-list-${sl.id}`}>
                            Enroll All
                          </Button>
                        </div>
                      ))
                    )}
                  </CardContent>
                  {pickerTab === 'contacts' && (
                    <div className="p-4 border-t border-white/10 flex items-center justify-between">
                      <span className="text-xs text-gray-400">{selectedContactIds.size} selected</span>
                      <Button size="sm" onClick={enrollContacts} disabled={selectedContactIds.size === 0} className="bg-purple-600 hover:bg-purple-700 text-white text-xs" data-testid="enroll-contacts-btn">
                        <UserPlus className="w-3 h-3 mr-1" /> Add to Campaign
                      </Button>
                    </div>
                  )}
                </Card>
              </div>
            )}
          </div>
        )}

        {/* CREATE CAMPAIGN */}
        {view === 'create' && (
          <div className="max-w-2xl" data-testid="create-campaign-view">
            <Card className="bg-white/[0.03] border-white/[0.06]">
              <CardHeader><CardTitle className="text-white flex items-center gap-2"><Sparkles className="w-5 h-5 text-purple-400" /> {editingCampaignId ? 'Edit Campaign' : 'New Outbound Campaign'}</CardTitle></CardHeader>
              <CardContent className="space-y-5">
                <div>
                  <Label className="text-gray-300 text-sm">Campaign Name</Label>
                  <Input value={newCampaign.name} onChange={e => setNewCampaign(p => ({ ...p, name: e.target.value }))} className="bg-black/30 border-white/10 text-white mt-1" data-testid="campaign-name" placeholder="e.g., Q1 Lead Outreach" />
                </div>

                {/* Campaign Type: Call / SMS / Email */}
                <div>
                  <Label className="text-gray-300 text-sm mb-2 block">Campaign Type</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { value: 'call', label: 'Phone Call', desc: 'AI voice calls contacts', icon: PhoneCall, on: 'bg-purple-500/10 border-purple-500/30', ic: 'text-purple-400' },
                      { value: 'sms', label: 'SMS', desc: 'Text message to contacts', icon: MessageSquare, on: 'bg-cyan-500/10 border-cyan-500/30', ic: 'text-cyan-400' },
                      { value: 'email', label: 'Email', desc: 'Email message to contacts', icon: Mail, on: 'bg-blue-500/10 border-blue-500/30', ic: 'text-blue-400' },
                    ].map(t => (
                      <button key={t.value} onClick={() => setNewCampaign(p => ({ ...p, campaign_type: t.value }))} data-testid={`campaign-type-${t.value}`}
                        className={`p-4 rounded-lg text-left transition-all border ${newCampaign.campaign_type === t.value ? t.on : 'bg-white/[0.02] border-white/[0.06] hover:border-white/10'}`}>
                        <t.icon className={`w-5 h-5 mb-2 ${newCampaign.campaign_type === t.value ? t.ic : 'text-gray-500'}`} />
                        <p className={`text-sm font-medium ${newCampaign.campaign_type === t.value ? 'text-white' : 'text-gray-400'}`}>{t.label}</p>
                        <p className="text-[10px] text-gray-500 mt-0.5">{t.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Mode: Bulk vs Drip */}
                <div>
                  <Label className="text-gray-300 text-sm mb-2 block">Campaign Mode</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { value: 'bulk', label: 'Bulk Call', desc: 'Call all contacts simultaneously', icon: Zap, color: 'orange' },
                      { value: 'drip', label: 'Drip Mode', desc: 'Call contacts one by one with intervals', icon: Clock, color: 'cyan' },
                    ].map(m => (
                      <button key={m.value} onClick={() => setNewCampaign(p => ({ ...p, mode: m.value }))} data-testid={`mode-${m.value}`}
                        className={`p-4 rounded-lg text-left transition-all border ${newCampaign.mode === m.value ? `bg-${m.color}-500/10 border-${m.color}-500/30` : 'bg-white/[0.02] border-white/[0.06] hover:border-white/10'}`}>
                        <m.icon className={`w-5 h-5 mb-2 ${newCampaign.mode === m.value ? `text-${m.color}-400` : 'text-gray-500'}`} />
                        <p className={`text-sm font-medium ${newCampaign.mode === m.value ? 'text-white' : 'text-gray-400'}`}>{m.label}</p>
                        <p className="text-[10px] text-gray-500 mt-0.5">{m.desc}</p>
                      </button>
                    ))}
                  </div>
                  {newCampaign.mode === 'drip' && (
                    <div className="mt-3">
                      <Label className="text-gray-400 text-xs">Interval between calls (minutes)</Label>
                      <Input type="number" min="5" value={newCampaign.drip_interval_minutes} onChange={e => setNewCampaign(p => ({ ...p, drip_interval_minutes: parseInt(e.target.value) || 30 }))} className="bg-black/30 border-white/10 text-white h-8 mt-1 w-32" data-testid="drip-interval" />
                    </div>
                  )}
                </div>

                {/* Schedule */}
                <div>
                  <Label className="text-gray-300 text-sm mb-2 block" data-testid="schedule-label">{newCampaign.campaign_type === 'call' ? 'Call Schedule' : 'Message Schedule'}</Label>
                  <div className="flex gap-3 items-center mb-2">
                    <div>
                      <Label className="text-gray-500 text-xs">Start</Label>
                      <Input type="time" value={newCampaign.send_window_start} onChange={e => setNewCampaign(p => ({ ...p, send_window_start: e.target.value }))} className="bg-black/30 border-white/10 text-white h-8 w-28" data-testid="schedule-start" />
                    </div>
                    <span className="text-gray-500 text-xs mt-4">to</span>
                    <div>
                      <Label className="text-gray-500 text-xs">End</Label>
                      <Input type="time" value={newCampaign.send_window_end} onChange={e => setNewCampaign(p => ({ ...p, send_window_end: e.target.value }))} className="bg-black/30 border-white/10 text-white h-8 w-28" data-testid="schedule-end" />
                    </div>
                  </div>
                  <div className="flex gap-1.5">
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
                      <button key={d} onClick={() => setNewCampaign(p => ({ ...p, send_days: p.send_days.includes(d) ? p.send_days.filter(x => x !== d) : [...p.send_days, d] }))} data-testid={`day-${d.toLowerCase()}`}
                        className={`px-2.5 py-1.5 rounded text-xs font-medium transition-all ${newCampaign.send_days.includes(d) ? 'bg-blue-600 text-white' : 'bg-white/[0.03] text-gray-500 hover:text-gray-300'}`}>{d}</button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-gray-300 text-sm">Goal</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    {[
                      { value: 'generate_leads', label: 'Generate Leads', icon: Target },
                      { value: 'book_meetings', label: 'Book Meetings', icon: Users },
                      { value: 're_engage', label: 'Re-engage', icon: Sparkles },
                      { value: 'follow_up', label: 'Follow Up', icon: PhoneCall },
                    ].map(g => (
                      <button key={g.value} onClick={() => setNewCampaign(p => ({ ...p, goal: g.value }))} data-testid={`goal-${g.value}`}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm transition-all border ${newCampaign.goal === g.value ? 'bg-blue-600/20 border-blue-500/30 text-blue-400' : 'bg-white/[0.02] border-white/[0.06] text-gray-400 hover:border-white/10'}`}>
                        <g.icon className="w-4 h-4" /> {g.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-gray-300 text-sm">Target Audience</Label>
                  <Textarea value={newCampaign.target_audience} onChange={e => setNewCampaign(p => ({ ...p, target_audience: e.target.value }))} className="bg-black/30 border-white/10 text-white mt-1" data-testid="target-audience" placeholder="e.g., SaaS founders in NYC..." />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-gray-300 text-sm">{newCampaign.campaign_type === 'call' ? 'AI Call Script' : 'Message Content'}</Label>
                    <Button variant="ghost" onClick={generateAIScript} disabled={generatingScript} className="text-xs text-purple-400 hover:text-purple-300 h-auto p-1" data-testid="generate-script-btn">
                      {generatingScript ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />} Generate with AI
                    </Button>
                  </div>
                  <Textarea value={newCampaign.ai_script} onChange={e => setNewCampaign(p => ({ ...p, ai_script: e.target.value }))} className="bg-black/30 border-white/10 text-white min-h-[120px]" data-testid="ai-script"
                    placeholder={newCampaign.campaign_type === 'call' ? 'AI will generate a personalized outreach script...' : `The ${newCampaign.campaign_type === 'sms' ? 'SMS' : 'email'} text sent to each contact...`} />
                  {newCampaign.campaign_type !== 'call' && (
                    <p className="text-[10px] text-gray-500 mt-1">Use {'{name}'} and {'{company}'} to personalize each message automatically.</p>
                  )}
                </div>

                <Button onClick={createCampaign} disabled={creating} className="bg-blue-600 hover:bg-blue-700 text-white w-full" data-testid="create-campaign-btn">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : editingCampaignId ? <Check className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />} {editingCampaignId ? 'Save Changes' : 'Create Campaign'}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default OutboundCalls;

